import React from 'react';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

import { useGetGlobalCustomersQuery } from 'feature/subscription-plans/enrollment/enrollmentEndpoints';
import AddressSection from './AddressSection';
import HoursOfOperation from './HoursOfOperation';
import Map from './Map';
import MiscInformation from './MiscInformation';
import SpecialHoursAccordion from './SpecialHoursAccordion';
import TerminalStatusChip from '../../terminal-locator/TerminalStatusChip';
import RightNow from '../../terminal-locator/RightNow';

const sectionContainerStyles = {
  display: 'flex',
  flexDirection: { md: 'row', xs: 'column' },
  pt: 2,
  px: { md: 5.5, xs: 0 },
};

const Overview = ({ terminalDetails }) => {
  const {
    address,
    city,
    currentBusynessLevel,
    irsCode,
    latitude,
    longitude,
    services,
    terminalPhoneNumber,
    splcCode,
    state,
    zipcode,
  } = terminalDetails;

  const { data: globalCustomersQuery = [] } = useGetGlobalCustomersQuery();
  const hasAffiliateWithSubscription = globalCustomersQuery.some(affiliate =>
    R.equals(affiliate.hasActiveSubscription, true)
  );

  const getServiceHours = service =>
    service?.hours || { saturday: '--', sunday: '--', weekday: '--' };

  const [loading, driverTraining, ethanolUnloading, transmixUnloading] =
    services.map(getServiceHours);

  return (
    <Box>
      <Box alignItems="center" display="flex" mb={1.5} pl={{ sm: 7, xs: 2 }} pr={{ sm: 0, xs: 1 }}>
        <Typography variant="h5">Overview</Typography>
      </Box>
      <Divider sx={{ mx: { sm: 7, xs: 2 } }} />

      <Box sx={sectionContainerStyles}>
        <Stack
          mr={{ lg: '12%', md: 4, xs: 0 }}
          pb={{ md: 0, xs: 2 }}
          pt={2}
          spacing={2}
          sx={{ minWidth: { md: '373px', xs: '100%' }, width: { md: '373px', xs: '100%' } }}
        >
          <AddressSection address={address} city={city} state={state} zipcode={zipcode} />
          <MiscInformation data={terminalPhoneNumber} label="Terminal Phone Number" />
          <HoursOfOperation
            saturdayHours={loading.saturday}
            sundayHours={loading.sunday}
            titleStyles={{ fontSize: 16, fontWeight: 300 }}
            titleVariant="subtitle1"
            weekdayHours={loading.weekday}
          />
          {hasAffiliateWithSubscription && !R.isNil(currentBusynessLevel) && (
            <Box sx={{ pl: 2, pr: 2 }}>
              <RightNow />
              <TerminalStatusChip status={currentBusynessLevel ?? 'Unknown'} />
            </Box>
          )}
          {services && services.length > 1 && (
            <SpecialHoursAccordion
              driverSatHours={driverTraining.saturday}
              driverSunHours={driverTraining.sunday}
              driverWeekdayHours={driverTraining.weekday}
              ethanolSatHours={ethanolUnloading.saturday}
              ethanolSunHours={ethanolUnloading.sunday}
              ethanolWeekdayHours={ethanolUnloading.weekday}
              tranmxSatHours={transmixUnloading.saturday}
              tranmxSunHours={transmixUnloading.sunday}
              tranmxWeekdayHours={transmixUnloading.weekday}
            />
          )}
          <MiscInformation data={splcCode ? `SPLC-${splcCode}` : '--'} label="SPLC Code" />
          <MiscInformation data={irsCode} label="IRS Code" />
        </Stack>
        <Map centerLatitude={latitude} centerLongitude={longitude} />
      </Box>
    </Box>
  );
};

Overview.propTypes = {
  terminalDetails: PropTypes.shape({
    address: PropTypes.string,
    city: PropTypes.string,
    currentBusynessLevel: PropTypes.string,
    irsCode: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    services: PropTypes.arrayOf(
      PropTypes.shape({
        hours: PropTypes.shape({
          // eslint-disable-next-line react/no-unused-prop-types
          saturday: PropTypes.string,
          // eslint-disable-next-line react/no-unused-prop-types
          sunday: PropTypes.string,
          // eslint-disable-next-line react/no-unused-prop-types
          weekday: PropTypes.string,
        }),
      })
    ),
    splcCode: PropTypes.string,
    state: PropTypes.string,
    terminalPhoneNumber: PropTypes.number,
    zipcode: PropTypes.string,
  }).isRequired,
};

export default Overview;
